/**
 * The flow's soft light: radial gradients that stand in for a blur.
 *
 * These used to be two-stop gradients under `filter: blur()`. The blur was by
 * far the most expensive thing on any screen — a 64px radius over a 320px box,
 * on every screen — and removing it is what made the flow usable on an older
 * phone. What the blur was also doing, though, is two things a plain gradient
 * doesn't:
 *
 *  1. Softening the falloff. A gradient interpolates alpha linearly between
 *     stops, so a two-stop blob has a visible "edge" partway out where the
 *     rate of change stops. `STOPS` below traces a bell curve instead, which
 *     is the shape a blur produces and the shape the eye reads as light.
 *
 *  2. Hiding banding. Any smooth low-alpha ramp bands on an 8-bit display:
 *     the steps between one output value and the next land far enough apart to
 *     read as rings. A blur dithers that away as a side effect. Nothing here
 *     can, so the shell lays a little noise over the top instead — see
 *     `NOISE_BACKGROUND`. It only matters once the gradient is large, which is
 *     why the rings appeared on desktop and not on a phone: the same number of
 *     steps spread over three times the distance makes each band three times
 *     wider.
 */

/**
 * Offsets and their share of the peak alpha, tracing a Gaussian-ish falloff.
 *
 * Eleven stops is more than it sounds: browsers interpolate between them, so
 * this is about the *shape*, not the resolution. Fewer than about eight and
 * the segments themselves start to show as facets on a large blob.
 */
const STOPS: readonly (readonly [number, number])[] = [
  [0, 1],
  [10, 0.96],
  [20, 0.86],
  [30, 0.72],
  [40, 0.57],
  [50, 0.42],
  [60, 0.28],
  [70, 0.17],
  [80, 0.085],
  [90, 0.03],
  [100, 0],
];

/**
 * A soft radial glow in one colour.
 *
 * @param rgb   the colour as bare `r,g,b` channels
 * @param peak  alpha at the centre
 *
 * Every stop is that colour at a fraction of `peak`, including the last one at
 * zero. Not the `transparent` keyword: that is transparent *black*, and
 * interpolating toward it drags a grey cast through the falloff of anything
 * warm.
 */
export function softGlow(rgb: string, peak: number): string {
  const stops = STOPS.map(
    ([at, share]) => `rgba(${rgb},${+(peak * share).toFixed(4)}) ${at}%`,
  );
  // `closest-side`, so 100% lands on the nearest edge of the box. A plain
  // `circle` sizes itself to the farthest *corner*: the ramp is then still
  // part-way up where the element's own round edge clips it, and the blob ends
  // in a hard rim. The old two-stop version got away with it by finishing at
  // 74% and leaving the rest of the box empty.
  return `radial-gradient(circle closest-side, ${stops.join(", ")})`;
}

/**
 * Fine noise, as a background image, for dithering the gradients above.
 *
 * An SVG `feTurbulence` rasterised once into an image — not a live filter on a
 * live layer, which is the thing that was making the flow slow. At the
 * opacities it is used at (3-4%) it is invisible as texture and does its whole
 * job by breaking up the boundary between two neighbouring bands.
 */
export const NOISE_BACKGROUND =
  `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)'/%3E%3C/svg%3E")`;
